import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { CardVariant, KnowledgeSmartCard } from '@atlassian/lumina/knowledge-smart-card';
import type { KnowledgeCardSource } from '@atlassian/lumina/knowledge-smart-card/types';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { useFetchSmartLinkData } from '../../../common/controller/use-fetch-smartlink-data/useFetchSmartLinkData';
import { useKnowledgeSmartCardAnalytics } from '../../../common/ui/knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import type { SmartLinkElement } from '../../dynamic-layout-parser/types/dynamic-layout-section';

interface SmartLinkComponentProps {
	component: SmartLinkElement;
	variant?: CardVariant;
}

const styles = cssMap({
	smartLink: {
		width: '336px',
	},
});

/**
 * Convert SmartLinkElement to KnowledgeCardSource format
 */
function convertToKnowledgeCardSource(
	component: SmartLinkElement,
	resolvedData?: any,
): KnowledgeCardSource {
	const url = component.attributes?.url || '';
	const title = component.attributes?.title;

	return {
		id: resolvedData?.['@id'] || url,
		title: title || resolvedData?.name || url,
		url,
		product: resolvedData?.generator?.name || null,
		type: resolvedData?.['@type'] || 'link',
		iconUrl: resolvedData?.generator?.icon?.url || null,
		lastModified: resolvedData?.dateModified || null,
		spaceName: null,
		spaceUrl: null,
		ari: resolvedData?.ari,
	};
}

export const DynamicLayoutSmartLink = ({
	component,
	variant,
}: SmartLinkComponentProps): React.JSX.Element | null => {
	const url = component.attributes?.url || '';
	const isActionCard = component.attributes?.type === 'action';
	const effectiveVariant = isActionCard ? CardVariant.ACTION_CARD : variant;

	const { objectResolverData } = useFetchSmartLinkData({ urls: [url] });
	const resolvedData = objectResolverData?.[0];

	const source = convertToKnowledgeCardSource(component, resolvedData);

	const context = useAIAnswerContext();
	const { handleSmartCardClick, searchSessionId } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
		isCitation: true,
	});

	return (
		<Box testId="dynamic-layout-smart-link">
			<KnowledgeSmartCard
				source={source}
				variant={effectiveVariant}
				searchSessionId={searchSessionId}
				onClickHandler={handleSmartCardClick}
			/>
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const DynamicLayoutStandaloneSmartLink = ({
	component,
	variant,
}: SmartLinkComponentProps): React.JSX.Element | null => {
	return (
		<Box xcss={styles.smartLink}>
			<DynamicLayoutSmartLink component={component} variant={variant} />
		</Box>
	);
};
